import { useId, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  AdjustmentsHorizontalIcon,
  BoltIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

import { SECTION_LABEL_SX } from '@/components/answerStyle/answerStyle.styles';
import { useAnswerStyleEditor } from '@/contexts/AnswerStyleEditorContext';
import { useAnswerStyle } from '@/hooks/useAnswerStyle';
import { BORDER_RADIUS, primitiveColors, semanticColors } from '@/theme/tokens';

import { ANSWER_DEPTHS, AnswerDepth } from './AnswerDepth';
import { lengthLabelKey, wordingLabelKey } from './AnswerStyle';

/**
 * A glyph per option, so the choice is legible before the words are read.
 *
 * The words alone asked a lot of a voter reading below high-school level: three
 * abstract nouns with no picture to hang them on. A bolt for "right away", a
 * magnifier for "looks things up", sparkles for "VoteMate picks" carry the gist
 * on their own, and the sentence underneath confirms it rather than being the
 * only way in.
 */
const DEPTH_ICONS: Record<AnswerDepth, typeof BoltIcon> = {
  auto: SparklesIcon,
  quick: BoltIcon,
  deep: MagnifyingGlassIcon,
};

/**
 * Response options: how VoteMate researches the next question, and how it
 * writes the answer.
 *
 * Under Research, each depth gets a glyph, a short name, and one short
 * sentence — a list of three bare nouns under no heading left a voter to infer
 * what the setting even was. Under Answer style, the voter's style and a Change
 * link that opens the answer-style editor.
 *
 * Sits beside the send button rather than above the input: `auto` is the right
 * choice almost always, so the control earns an icon rather than permanent
 * vertical space in the chat column. The trigger is the chosen option's own
 * glyph plus a chevron — sparkles, bolt, or magnifier, the same mark that
 * option carries in the menu. A lone word ("Normal") named a setting without
 * saying it could be changed; the glyph says "options here" and, being the
 * menu's own mark, teaches which one is on. It stays caption grey in every
 * state — the shape carries the state, so a tint change would only make the
 * composer louder. The depth sticks for the rest of the session — someone
 * comparing several candidates in depth should not have to re-pick per message
 * — and resets to `auto` on reload, so a forgotten `deep` cannot quietly slow
 * every future answer.
 */

// MUI tints a selected row with the theme's blue; the check carries the state,
// so the row keeps the neutral surface the hover uses.
const SELECTED_ROW_SX = {
  '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: semanticColors.surfaceWarm },
} as const;

interface AnswerDepthSelectorProps {
  depth: AnswerDepth;
  onChange: (depth: AnswerDepth) => void;
  disabled?: boolean;
}

export default function AnswerDepthSelector({
  depth,
  onChange,
  disabled,
}: AnswerDepthSelectorProps) {
  const { t } = useTranslation('chat');
  const { t: tc } = useTranslation('common');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const titleId = useId();
  const open = Boolean(anchorEl);
  const TriggerIcon = DEPTH_ICONS[depth];
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // How much VoteMate looks up and how it writes are separate choices, each
  // under its own heading. Mirrors the mobile selector.
  const answerStyle = useAnswerStyle();
  const { openAnswerStyleEditor } = useAnswerStyleEditor();
  const labelKey = (d: AnswerDepth) => `input.responseOptions.options.${d}.label`;
  const hintKey = (d: AnswerDepth) => `input.responseOptions.options.${d}.hint`;
  const styleSummary = tc('answerStyle.summary', {
    wording: tc(wordingLabelKey(answerStyle.style.wording)),
    length: tc(lengthLabelKey(answerStyle.style.length)),
  });
  const menuTitle = t('input.responseOptions.title');

  const handleChangeStyle = () => {
    setAnchorEl(null);
    // Focus returns to the trigger when the editor closes; the menu item that
    // opened it is gone by then.
    openAnswerStyleEditor('composer', triggerRef.current);
  };

  const handleSelect = (next: AnswerDepth) => {
    setAnchorEl(null);
    // Re-picking the current depth is a no-op rather than an event: the
    // analytics question is which depths voters move to, and a confirm-the-
    // status-quo tap would inflate that with non-changes.
    if (next !== depth) onChange(next);
  };

  return (
    <>
      <Tooltip title={menuTitle}>
        {/* span keeps the tooltip working while the button is disabled */}
        <span>
          <Button
            ref={triggerRef}
            onClick={(event) => setAnchorEl(event.currentTarget)}
            disabled={disabled}
            size="small"
            aria-label={t('input.responseOptions.ariaLabel', {
              depth: t(labelKey(depth)),
              summary: styleSummary,
            })}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? menuId : undefined}
            startIcon={<TriggerIcon style={{ width: 18, height: 18 }} aria-hidden="true" />}
            endIcon={<ChevronDownIcon style={{ width: 14, height: 14 }} aria-hidden="true" />}
            // A soft pill, sized to the send button beside it: the fill says
            // "this is a control" without the visual weight of an outline.
            sx={{
              minWidth: 0,
              height: 36,
              minHeight: 36,
              // Shorthand on purpose — the global MuiButton override sets a
              // shorthand ``padding`` that would otherwise win the tie.
              padding: '0 10px',
              borderRadius: '999px',
              textTransform: 'none',
              backgroundColor: semanticColors.surfaceWarm,
              '& .MuiButton-startIcon': { mr: 0, ml: 0 },
              '& .MuiButton-endIcon': { ml: '2px' },
              // Both glyphs inherit this. One caption grey in every state: the
              // glyph's own shape says which setting is on, so a second signal
              // in the brand tint only made the composer louder.
              color: semanticColors.textCaption,
              transition: 'background-color 0.2s ease, color 0.2s ease',
              '&:hover': {
                backgroundColor: primitiveColors.gray3,
                color: semanticColors.textBody,
              },
              '&.Mui-disabled': {
                backgroundColor: semanticColors.surfaceWarm,
                color: semanticColors.disabled,
              },
              '&:focus-visible': {
                outline: `2px solid ${semanticColors.brandPrimary}`,
                outlineOffset: '2px',
              },
            }}
          />
        </span>
      </Tooltip>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{
          // Sized like the Sources pop-up beside it: 280px, 13/12/11 type.
          paper: {
            sx: { borderRadius: BORDER_RADIUS, width: 280, maxWidth: 'calc(100vw - 32px)' },
          },
          list: {
            'aria-labelledby': titleId,
            // MenuItem gives its icon a 36px box; the rows size their own gap.
            sx: { pt: 0, '&& .MuiListItemIcon-root': { minWidth: 0 } },
          },
        }}
      >
        <ListSubheader
          id={titleId}
          // `presentation`, so the heading is not exposed as a fourth child of
          // `role="menu"` — it names the menu through `aria-labelledby`
          // instead, which reads it whether or not it is in the item list.
          role="presentation"
          disableSticky
          sx={{
            pt: 1,
            pb: 0.5,
            px: 1.5,
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.3,
            whiteSpace: 'normal',
            color: semanticColors.textPrimary,
          }}
        >
          {menuTitle}
        </ListSubheader>
        <ListSubheader
          role="presentation"
          disableSticky
          sx={[SECTION_LABEL_SX, { pt: 0.75, pb: 0.25, px: 1.5, whiteSpace: 'normal' }]}
        >
          {t('input.responseOptions.research')}
        </ListSubheader>
        {ANSWER_DEPTHS.map((option) => {
          const selected = option === depth;
          const Icon = DEPTH_ICONS[option];
          return (
            <MenuItem
              key={option}
              // radio semantics, not a plain menu item: these are three states of
              // one setting, and a screen reader should say which one is active.
              role="menuitemradio"
              aria-checked={selected}
              selected={selected}
              onClick={() => handleSelect(option)}
              sx={[
                { alignItems: 'center', gap: 1, py: 0.75, px: 1.5, minHeight: 0 },
                SELECTED_ROW_SX,
              ]}
            >
              <ListItemIcon sx={{ minWidth: 0 }}>
                <Icon
                  aria-hidden="true"
                  style={{
                    width: 16,
                    height: 16,
                    color: selected ? primitiveColors.rockSlate : semanticColors.textCaption,
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={t(labelKey(option))}
                secondary={t(hintKey(option))}
                slotProps={{
                  primary: {
                    sx: {
                      fontSize: 12,
                      fontWeight: 600,
                      color: semanticColors.textPrimary,
                    },
                  },
                  // `whiteSpace: normal` because a MenuItem sets `nowrap`, and
                  // a one-sentence hint is the point of the row.
                  secondary: { sx: { fontSize: 11, lineHeight: 1.4, whiteSpace: 'normal' } },
                }}
              />
              {/* A check, not only the selected row's tint: MUI's `selected`
                  background is the same tint as hover, so on a pointer device
                  the active option was indistinguishable from the one under the
                  cursor. The space is reserved either way, so picking a
                  different option moves nothing but the check. */}
              <Box sx={{ width: 16, flex: '0 0 auto', display: 'flex' }}>
                {selected && (
                  <CheckIcon
                    aria-hidden="true"
                    style={{ width: 16, height: 16, color: primitiveColors.rockSlate }}
                  />
                )}
              </Box>
            </MenuItem>
          );
        })}
        <Divider component="li" role="presentation" sx={{ my: 0.5 }} />
        <ListSubheader
          role="presentation"
          disableSticky
          sx={[SECTION_LABEL_SX, { pt: 0.75, pb: 0.25, px: 1.5, whiteSpace: 'normal' }]}
        >
          {t('input.responseOptions.answerStyle')}
        </ListSubheader>
        <MenuItem
          onClick={handleChangeStyle}
          aria-label={t('input.responseOptions.changeLabel', { summary: styleSummary })}
          // The research rows' own metrics, so the two groups read as one menu.
          sx={{ alignItems: 'center', gap: 0.75, py: 0.75, px: 1.5, minHeight: 0, mb: 0.5 }}
        >
          <ListItemIcon sx={{ minWidth: 0 }}>
            <AdjustmentsHorizontalIcon
              aria-hidden="true"
              style={{ width: 16, height: 16, color: semanticColors.textCaption }}
            />
          </ListItemIcon>
          <ListItemText
            primary={styleSummary}
            slotProps={{
              primary: {
                sx: {
                  // A value rather than a heading, and a 500 weight keeps the
                  // English summary on one line at the pop-up's 280px.
                  fontSize: 12,
                  fontWeight: 500,
                  whiteSpace: 'normal',
                  color: semanticColors.textPrimary,
                },
              },
            }}
          />
          <Box
            component="span"
            sx={{
              flex: '0 0 auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '2px',
              fontSize: 11,
              fontWeight: 600,
              color: semanticColors.textPrimary,
              // The chevron points the way the row reads.
              '[dir="rtl"] & svg': { transform: 'scaleX(-1)' },
            }}
          >
            {t('input.responseOptions.change')}
            <ChevronRightIcon
              aria-hidden="true"
              style={{ width: 12, height: 12, strokeWidth: 2.5 }}
            />
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
}
